import Store from 'repatch';

import State from '../core/State';
import Account, { AccountKind } from '../entity/Account';
import { setNewAccountStatus } from '../reducer/account';
import { finishSpinner, startSpinner } from '../reducer/fullScreenSpinner';
import { setAccount } from '../reducer/organization';
import ApiErrorHandler from '../service/ApiErrorHandler';
import MessageDispatcher from '../service/MessageDispatcher';
import AccountWebApi from '../service/persistent/AccountWebApi';
import OrganizationUsecase from './OrganizationUsecase';

class AccountUsecase {
  private store: Store<State>;
  private accountWebApi: AccountWebApi;
  private messageDispatcher: MessageDispatcher;
  private apiErrorHandler: ApiErrorHandler;
  private organizationUsecase: OrganizationUsecase;

  constructor({
    store,
    accountWebApi,
    messageDispatcher,
    apiErrorHandler,
    organizationUsecase,
  }: {
    store: Store<State>;
    accountWebApi: AccountWebApi;
    messageDispatcher: MessageDispatcher;
    apiErrorHandler: ApiErrorHandler;
    organizationUsecase: OrganizationUsecase;
  }) {
    this.store = store;
    this.accountWebApi = accountWebApi;
    this.messageDispatcher = messageDispatcher;
    this.apiErrorHandler = apiErrorHandler;
    this.organizationUsecase = organizationUsecase;
  }

  public getAccount = async (id: string): Promise<void> => {
    const account = await this.accountWebApi.getAccount(id);
    this.store.dispatch(setAccount(account));
  };

  public updateAccount = async ({
    id,
    companyName,
    representativeName,
    companyUrl,
    departmentName,
    tel,
    kind,
  }: {
    id: string;
    companyName: string;
    representativeName: string;
    companyUrl: string;
    departmentName: string;
    tel: string;
    kind: AccountKind;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.accountWebApi.updateAccount({
        id,
        companyName,
        representativeName,
        companyUrl,
        departmentName,
        tel,
        kind,
      });
      await this.getAccount(id);
      await this.organizationUsecase.reloadCurrentOrganizations(id);
      this.messageDispatcher.notifyUnimportant('organization_setting.account.settings_updated');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public createAccount = async ({
    companyName,
    representativeName,
    companyUrl,
    departmentName,
    tel,
    country,
    kind,
  }: {
    companyName: string;
    representativeName: string;
    companyUrl: string;
    departmentName: string;
    tel: string;
    country: string;
    kind: AccountKind;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const status = await this.accountWebApi.createAccount({
        companyName,
        representativeName,
        companyUrl,
        departmentName,
        tel,
        country,
        kind,
      });
      this.store.dispatch(setNewAccountStatus(status));
      this.messageDispatcher.notifyUnimportant('account_setting.new.created');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public createAccountWithQuestionnaire = async ({
    companyName,
    representativeName,
    companyUrl,
    departmentName,
    tel,
    country,
    kind,
    questionnaireAnswer,
    isGlobal,
  }: {
    companyName: string;
    representativeName: string;
    companyUrl: string;
    departmentName: string;
    tel: string;
    country: string;
    kind: AccountKind;
    questionnaireAnswer?: string;
    isGlobal?: boolean;
  }): Promise<Account> => {
    try {
      this.store.dispatch(startSpinner());
      const account = await this.accountWebApi.createAccountWithQuestionnaire({
        companyName,
        representativeName,
        companyUrl,
        departmentName,
        tel,
        country,
        kind,
        questionnaireAnswer,
        isGlobal,
      });
      this.store.dispatch(setNewAccountStatus(account.status));
      return account;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };
}

export default AccountUsecase;
